import React, { useState } from 'react';
import { Lock, Shield, Smartphone, LogOut } from 'lucide-react';

const SecuritySettings = () => {
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleChangePassword = () => {
        // Mock API call
        console.log('Changing password:', passwordForm);
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            alert('Passwords do not match!');
            return;
        }
        alert('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
    };

    const handleToggle2FA = () => {
        // Mock API call
        const newStatus = !twoFactorEnabled;
        setTwoFactorEnabled(newStatus);
        alert(`Two-Factor Authentication ${newStatus ? 'enabled' : 'disabled'} successfully!`);
    };

    const handleLogoutAllDevices = () => {
        // Mock API call
        console.log('Logging out from all devices');
        setShowLogoutModal(false);
        alert('Logged out from all devices successfully!');
    };

    return (
        <div className="h-full flex flex-col p-2 md:p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex-shrink-0 mb-2 md:mb-3">
                <h2 className="text-lg md:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Security Settings
                </h2>
                <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">Manage your account security and authentication</p>
            </div>

            {/* Security Options */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                <div className="space-y-2 md:space-y-3">
                    {/* Change Password */}
                    <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 p-3 md:p-4 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 md:gap-4">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg md:rounded-xl flex items-center justify-center">
                                    <Lock size={20} className="text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-bold text-gray-900">Change Password</h3>
                                    <p className="text-[10px] md:text-xs text-gray-600">Update your account password</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowPasswordModal(true)}
                                className="px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-md hover:shadow-lg text-xs md:text-sm"
                            >
                                Change
                            </button>
                        </div>
                    </div>

                    {/* Two-Factor Authentication */}
                    <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 p-3 md:p-4 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 md:gap-4">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg md:rounded-xl flex items-center justify-center">
                                    <Smartphone size={20} className="text-green-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-bold text-gray-900">Two-Factor Authentication</h3>
                                    <p className="text-[10px] md:text-xs text-gray-600">
                                        Add an extra layer of security to your account
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 md:gap-3">
                                <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-bold ${twoFactorEnabled
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-700'
                                    }`}>
                                    {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <button
                                    onClick={handleToggle2FA}
                                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-all font-semibold shadow-md hover:shadow-lg text-xs md:text-sm ${twoFactorEnabled
                                        ? 'bg-red-600 text-white hover:bg-red-700'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                        }`}
                                >
                                    {twoFactorEnabled ? 'Disable' : 'Enable'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Logout of All Devices */}
                    <div className="bg-white rounded-lg md:rounded-xl border border-red-200 p-3 md:p-4 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 md:gap-4">
                                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-lg md:rounded-xl flex items-center justify-center">
                                    <Shield size={20} className="text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-bold text-gray-900">Logout of All Devices</h3>
                                    <p className="text-[10px] md:text-xs text-gray-600">
                                        Sign out from all devices
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowLogoutModal(true)}
                                className="px-3 md:px-4 py-1.5 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold shadow-md hover:shadow-lg text-xs md:text-sm"
                            >
                                Logout All
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                <Lock size={24} className="text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
                                <p className="text-sm text-gray-600">Enter your current and new password</p>
                            </div>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Current Password
                                </label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={passwordForm.currentPassword}
                                    onChange={handlePasswordChange}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    placeholder="Enter current password"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={passwordForm.newPassword}
                                    onChange={handlePasswordChange}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    placeholder="Enter new password"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Confirm New Password
                                </label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    placeholder="Confirm new password"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowPasswordModal(false);
                                    setPasswordForm({
                                        currentPassword: '',
                                        newPassword: '',
                                        confirmPassword: ''
                                    });
                                }}
                                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleChangePassword}
                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
                            >
                                Change Password
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Logout All Devices Modal */}
            {showLogoutModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <LogOut size={32} className="text-red-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Logout of All Devices?</h3>
                            <p className="text-sm text-gray-600">
                                You will be signed out from all devices. You'll need to log in again on each device.
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowLogoutModal(false)}
                                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLogoutAllDevices}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold"
                            >
                                Logout All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecuritySettings;
